import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import type { SelectOption } from '@kula-ai/components';
import { Tabs, type TabItem } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../AddInterviewActivity.interface';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from './communication.types';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from './helpers/payloadMap';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from './communication.state';
import {
  getFirstCommunicationErrorScrollTarget,
  scrollCommunicationSectionIntoView,
} from './communication.scroll';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from './communication.validate';
import { calendarInviteTemplateNameForApi } from './helpers/calendarInviteName';
import { compositionFromTemplateOption } from './helpers/templateApply';
import { CandidateCommunicationPanel } from './panels/CandidateCommunicationPanel';
import { InterviewerCommunicationPanel } from './panels/InterviewerCommunicationPanel';
import { SelfScheduleCommunicationPanel } from './panels/SelfScheduleCommunicationPanel';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import {
  useInterviewActivityCommunicationVariables,
  useInterviewCommunicationSenderOptions,
  useInterviewEmailTemplateOptions,
} from './api';

/** Delegatable users plus a non-id “Organizer” row (resolved at send time; see `from_organizer` in API). */
function useFromAndCcBccOptions(delegatableOptions: SelectOption[]) {
  return useMemo(() => {
    const organizer: SelectOption = {
      value: INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE,
      label: 'Organizer email',
      description:
        'Whoever is scheduling the interview will be prefilled as the sender',
    };
    const rest = delegatableOptions.filter(
      (o) => o.value !== INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
    );
    return [organizer, ...rest];
  }, [delegatableOptions]);
}

export const InterviewActivityCommunication = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, prefillVersion = 0 }, ref) => {
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            payloadRef.current.communication,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      const { data: candidateTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate');
      const { data: interviewerTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer');
      const { data: selfScheduleTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      const { data: delegatableRaw = [] } =
        useInterviewCommunicationSenderOptions();
      const mergedParticipantOptions = useFromAndCcBccOptions(delegatableRaw);

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
        selfScheduleEmail: selfScheduleVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();

      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
          selfSchedule: selfScheduleVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
          selfScheduleVariables,
        ]
      );

      /** After a failed submit, keep error UI in sync while the user fixes fields. */
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) {
            return prev;
          }
          return validateInterviewCommunicationForm(state, validationVariables);
        });
      }, [state, validationVariables]);

      const panelVariableProps = useMemo(
        () => ({
          candidateCalendarVariables,
          interviewerCalendarVariables,
          candidateEmailVariables,
          interviewerEmailVariables,
          selfScheduleVariables,
          uploadInlineImage,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
          selfScheduleVariables,
          uploadInlineImage,
        ]
      );

      /** One-shot: when ATS templates load, preselect the first row per block (if still unset). */
      const didDefaultCandidateCal = useRef(false);
      const didDefaultInterviewerCal = useRef(false);
      const didDefaultCandidateEmail = useRef(false);
      const didDefaultInterviewerEmail = useRef(false);
      const didDefaultSelfSchedule = useRef(false);

      useEffect(() => {
        /** Edit hydrate (prefillVersion > 0): do not overlay first template subject/body — it overrides saved comms. */
        if (prefillVersion > 0) {
          return;
        }
        if (!candidateTemplateOptions.length) return;
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        candidateTemplateOptions,
        state.candidateCalendar,
        dispatch,
      ]);

      useEffect(() => {
        if (prefillVersion > 0) {
          return;
        }
        if (!interviewerTemplateOptions.length) return;
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        dispatch,
        interviewerTemplateOptions,
        state.interviewerCalendar,
      ]);

      useEffect(() => {
        if (prefillVersion > 0) {
          return;
        }
        if (!state.candidateEmailEnabled || !candidateTemplateOptions.length) {
          return;
        }
        if (state.candidateEmail.template) {
          didDefaultCandidateEmail.current = true;
          return;
        }
        if (didDefaultCandidateEmail.current) return;
        didDefaultCandidateEmail.current = true;
        dispatch({
          type: 'SET_CANDIDATE_EMAIL',
          payload: compositionFromTemplateOption(
            state.candidateEmail,
            candidateTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        candidateTemplateOptions,
        dispatch,
        state.candidateEmail,
        state.candidateEmailEnabled,
      ]);

      useEffect(() => {
        if (prefillVersion > 0) {
          return;
        }
        if (
          !state.interviewerEmailEnabled ||
          !interviewerTemplateOptions.length
        ) {
          return;
        }
        if (state.interviewerEmail.template) {
          didDefaultInterviewerEmail.current = true;
          return;
        }
        if (didDefaultInterviewerEmail.current) return;
        didDefaultInterviewerEmail.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_EMAIL',
          payload: compositionFromTemplateOption(
            state.interviewerEmail,
            interviewerTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        dispatch,
        interviewerTemplateOptions,
        state.interviewerEmail,
        state.interviewerEmailEnabled,
      ]);

      useEffect(() => {
        if (prefillVersion > 0) {
          return;
        }
        if (!state.selfScheduleEnabled || !selfScheduleTemplateOptions.length) {
          return;
        }
        if (state.selfSchedule.template) {
          didDefaultSelfSchedule.current = true;
          return;
        }
        if (didDefaultSelfSchedule.current) return;
        didDefaultSelfSchedule.current = true;
        dispatch({
          type: 'SET_SELF_SCHEDULE',
          payload: compositionFromTemplateOption(
            state.selfSchedule,
            selfScheduleTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        dispatch,
        selfScheduleTemplateOptions,
        state.selfSchedule,
        state.selfScheduleEnabled,
      ]);

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables
            );
            setFieldErrors(errors);
            if (!hasValidationErrors(errors)) {
              return true;
            }
            const target = getFirstCommunicationErrorScrollTarget(errors);
            if (target) {
              const needsTabSwitch = state.activeSubTab !== target.tab;
              if (needsTabSwitch) {
                dispatch({
                  type: 'SET_ACTIVE_SUB_TAB',
                  payload: target.tab,
                });
              }
              const delayMs = needsTabSwitch ? 160 : 0;
              window.setTimeout(() => {
                scrollCommunicationSectionIntoView(target.sectionId);
              }, delayMs);
            }
            return false;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state);
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerTemplateOptions
              );
            payloadRef.current.communication = comm;
          },
        }),
        [
          candidateTemplateOptions,
          dispatch,
          interviewerTemplateOptions,
          payloadRef,
          state,
          validationVariables,
        ]
      );

      const tabItems: TabItem[] = useMemo(
        () => [
          {
            label: 'Candidate communication',
            value: 'candidate-communication',
            panel: (
              <CandidateCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={candidateTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Interviewer communication',
            value: 'interviewer-communication',
            panel: (
              <InterviewerCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={interviewerTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Self-schedule slot selection email',
            value: 'self-schedule-slot-selection-email',
            panel: (
              <SelfScheduleCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={selfScheduleTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
        ],
        [
          state,
          dispatch,
          fieldErrors,
          candidateTemplateOptions,
          interviewerTemplateOptions,
          selfScheduleTemplateOptions,
          mergedParticipantOptions,
          panelVariableProps,
        ]
      );

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          minW={0}
          flex="1"
          flexDirection="column"
          overflow="hidden"
        >
          <Tabs.Root
            variant="side"
            items={tabItems}
            value={state.activeSubTab}
            onValueChange={(next) => {
              dispatch({
                type: 'SET_ACTIVE_SUB_TAB',
                payload: next as typeof state.activeSubTab,
              });
            }}
            className="h-full min-h-0 w-full min-w-0 flex-1 grid-rows-[minmax(0,1fr)] gap-3"
            tabListWrapperClassName="self-start"
            panelClassName="min-h-0 h-full max-h-full min-w-0 overflow-y-auto overflow-x-hidden pb-1 pr-4"
          />
        </Flex>
      );
    }
  )
);

InterviewActivityCommunication.displayName = 'InterviewActivityCommunication';
